import { useMemo, useRef, useState, useEffect } from "react";
import { useTable, useSortBy, Column, SortingRule } from "react-table";
import { useJSONFetcher } from "@outplayed/json-fetcher";
import classnames from "classnames";
import "../../../stylesheets/components/TierlistStyles.scss";
import { getDeadlockTierColor, getDeadlockTierLetter } from "lib/tierHelper";
import { getDeadlockImagePath, getWinRateColor } from "lib/general-helper";
import { useLazyLoadTableRows, TIER_LIST_PAGE_SIZE } from "@ugg/shared/components/TierList/common/TableScrollHandler";
import { TierListTablePlaceholder } from "@ugg/shared/components/TierList/common/TierListTablePlaceholder";
import { Link } from "react-router-dom";
import { useQueryString } from "hooks/general-hooks";
const S3_IMG_BASE_URL = "https://s3.us-east-2.amazonaws.com/static.bigbrain.gg/deadlock/staticdata/panorama/images";

interface HeroInfo {
  HeroName: string;
  Tier: number;
  WinPercentage: number;
  PickPercentage: number;
  KDA: number;
  NumGames: number;
}

interface TierlistTableProps {
  heroFilterText: string;
}

export const TierlistTable = (props: TierlistTableProps) => {
  const [pageSize, setPageSize] = useState(TIER_LIST_PAGE_SIZE);
  const prevSort = useRef<SortingRule<HeroInfo>[] | null>(null);

  const { lane }: { [key: string]: any } = useQueryString();

  let tierListJSON = "combined_tierlist";
  let selectedLane = "combined";
  if (lane === "single") {
    tierListJSON = "single_tierlist";
    selectedLane = "single";
  }
  if (lane === "duo") {
    tierListJSON = "duo_tierlist";
    selectedLane = "duo";
  }

  const {
    data: tierlistData,
    loading: tierlistDataLoading,
    error: tierlistDataError,
  } = useJSONFetcher<Array<HeroInfo>>(
    `https://s3.us-east-2.amazonaws.com/stats2.u.gg/Deadlock/final_tierlist/current/${tierListJSON}.json`,
    {
      ssr: true,
    },
  );

  const {
    data: allHerosStaticData,
    loading: allHerosStaticDataLoading,
    error: allHerosStaticDataError,
  } = useJSONFetcher("https://static.bigbrain.gg/deadlock/staticdata/gamedata/Heroes_English.json", { ssr: true });

  const tableData = useMemo(() => {
    if (tierlistData && props.heroFilterText) {
      return tierlistData.filter((x) => x.HeroName.toLowerCase().indexOf(props.heroFilterText.toLowerCase()) >= 0) || [];
    } else {
      return tierlistData || [];
    }
  }, [tierlistData, props.heroFilterText]);

  const getHeroImage = (name: string) => {
    const heroName = name.toLowerCase().replace(/\s/g, "_");
    return getDeadlockImagePath(allHerosStaticData?.[heroName]?.IconHeroCard);
  };

  const columns = useMemo<Column<HeroInfo>[]>(
    () => [
      {
        id: "rank",
        accessor: "HeroName",
        Header: <span>Rank</span>,
        className: "rank",
        Cell: ({ index }) => <span>{index + 1}</span>,
        sortable: false,
        responsiveClassName: "xxxs:w-[40px] xs:w-[60px] xxxs:min-w-[40px] xs:min-w-[40px] xxxs:max-w-[40px] xs:max-w-[60px]",
      },
      {
        Header: "Hero",
        accessor: "HeroName",
        className: "champion",
        Cell: ({ value }) => {
          const formatHeroName = (name: string) => {
            if (name === "THE MAGNIFICENT SINCLAIR") {
              return name
                .toLowerCase()
                .split(" ")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ");
            }
            return name;
          };
          return (
            <Link
              className="flex items-center gap-2"
              to={{
                pathname: `/deadlock/heroes/${value.toLowerCase().replace("&", "and").replace(/\s/g, "_")}/build`,
              }}
            >
              <img
                src={`${S3_IMG_BASE_URL}${getHeroImage(value)}.png`}
                alt={formatHeroName(value)}
                className="w-[30px] h-[30px] rounded-sm"
              />
              <div className="max-[505px]:hidden">{formatHeroName(value)}</div>
            </Link>
          );
        },
        responsiveClassName: `
          xxxs:min-w-[40px] xs:min-w-[100px]
          flex-grow flex-1
          max-[800px]:!justify-start
          min-[800px]:!justify-start
          max-[600px]:!min-w-[40px]
        `,
      },
      {
        Header: "Tier",
        accessor: "Tier",
        className: "tier",
        Cell: ({ value }) => <span style={{ color: getDeadlockTierColor(value) }}>{getDeadlockTierLetter(value)}</span>,
        responsiveClassName: "xxxs:w-[40px] xs:w-[60px] xxxs:min-w-[40px] xs:min-w-[40px] xxxs:max-w-[40px] xs:max-w-[60px]",
      },
      {
        id: "winrate",
        Header: "Win Rate",
        accessor: "WinPercentage",
        responsiveClassName: "xxxs:min-w-[60px] max-xs:max-w-[80px] xs:min-w-[70px] max-w-[90px]",
        headerClassName: "winrate",
        className: "winrate",
        sortDescFirst: true,
        sortType: (a, b) => {
          return a.original.WinPercentage - b.original.WinPercentage;
        },
        Cell: ({ value }) => (
          <span style={{ color: getWinRateColor(Number((value * 100).toFixed(1))) }}>
            <b>{(value * 100).toFixed(1) + "%"}</b>
          </span>
        ),
      },
      {
        Header: "Pick Rate",
        accessor: "PickPercentage",
        responsiveClassName: "xxxs:max-xxs:!hidden xxxs:min-w-[60px] xs:min-w-[80px] xxxs:max-w-[60px] xs:max-w-[90px]",
        headerClassName: "pickrate",
        className: "pickrate",
        sortDescFirst: true,
        sortType: (a, b) => {
          return a.original.PickPercentage - b.original.PickPercentage;
        },
        Cell: ({ value }) => (
          <span>
            <b>{(value * 100).toFixed(1) + "%"}</b>
          </span>
        ),
      },
      {
        Header: "KDA",
        accessor: "KDA",
        responsiveClassName: "xxxs:min-w-[60px] max-xs:max-w-[70px] xs:min-w-[80px] max-w-[90px]",
        headerClassName: "kda",
        className: "kda",
        sortDescFirst: true,
        sortType: (a, b) => {
          return a.original.KDA - b.original.KDA;
        },
        Cell: ({ value }) => <span>{value}</span>,
      },
      {
        Header: "Matches",
        accessor: "NumGames",
        responsiveClassName: "xxxs:max-xxs:!hidden xxxs:min-w-[60px] xs:min-w-[80px] xxxs:max-w-[60px] xs:max-w-[90px]",
        headerClassName: "matches",
        className: "matches",
        sortDescFirst: true,
        sortType: (a, b) => {
          return a.original.NumGames - b.original.NumGames;
        },
        Cell: ({ value }) => <span>{value}</span>,
      },
    ],
    [allHerosStaticData, selectedLane],
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, state } = useTable(
    {
      columns,
      data: tableData,
      initialState: {
        sortBy: prevSort.current || [{ id: "winrate", desc: true }],
      },
      disableSortRemove: true,
    },
    useSortBy,
  );

  const tableRef = useRef<HTMLDivElement>(null);
  useLazyLoadTableRows({
    tableData,
    tableRef,
    pageSize,
    setPageSize,
  });

  const columnStyle = () => ({
    // Will have to change this as more modes are added
    maxWidth: undefined,
    flex: `1 1 100%`,
  });

  useEffect(() => {
    if (prevSort.current) {
      if (prevSort.current[0].id !== state.sortBy[0].id || prevSort.current[0].desc !== state.sortBy[0].desc) {
        prevSort.current = state.sortBy;
        setPageSize(TIER_LIST_PAGE_SIZE);
      }
    } else {
      prevSort.current = state.sortBy;
    }
  }, [state.sortBy]);

  if (tierlistDataLoading || allHerosStaticDataLoading) {
    return <TierListTablePlaceholder />;
  }

  if (tierlistDataError || allHerosStaticDataError) {
    return <div>Error: {tierlistDataError?.message || allHerosStaticDataError?.message}</div>;
  }

  return (
    <div className="w-full overflow-x-auto rounded-sm bg-purple-400">
      <div
        ref={tableRef}
        {...getTableProps({ className: "content-section ReactTable ugg-table-2 tierlist-table min-w-[400px]" })}
      >
        <div className="rt-thead -header">
          {headerGroups.map((headerGroup, index) => (
            <div {...headerGroup.getHeaderGroupProps({ className: "rt-tr" })}>
              {headerGroup.headers.map((column, index) => {
                const thClassNames = classnames([
                  "rt-th",
                  "px-12",
                  column.headerClassName,
                  column.responsiveClassName,
                  column.isSorted && !column.isSortedDesc ? "-sort-asc" : "",
                  column.isSorted && column.isSortedDesc ? "-sort-desc" : "",
                ]);

                return (
                  <div
                    {...column.getHeaderProps(
                      column.getSortByToggleProps({
                        style: columnStyle(),
                      }),
                    )}
                    className={thClassNames}
                  >
                    {column.render("Header")}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div {...getTableBodyProps({ className: "rt-tbody" })}>
          {rows.map((row, i) => {
            if (i >= pageSize) {
              return null;
            }

            prepareRow(row);

            return (
              <div key={i} className="rt-tr-group">
                <div {...row.getRowProps({ className: "rt-tr" })}>
                  {row.cells.map((cell) => {
                    const tdClassNames = classnames(
                      "rt-td",
                      cell.column.responsiveClassName,
                      cell.column.className,
                      {
                        sorted: cell.column.isSorted,
                        "is-in-odd-row": i % 2 === 0,
                      },
                      "px-12",
                    );

                    return (
                      <div
                        {...cell.getCellProps({
                          className: tdClassNames,
                        })}
                      >
                        {cell.render("Cell", { index: i })}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* <TableScrollHandler /> */}
    </div>
  );
};
